import { useTranslation } from "react-i18next";

import { Text } from "chameleon/lib/components";

import {
  cartItemImageOverride,
  cartItemImageWrapper,
  cartItemInfo,
  cartItemPriceStringWrapper,
  cartItemPriceWrapper,
  cartItemsNonFirstRow,
  cartItemsRow,
  cartItemsTable,
} from "checkout-experience/components/cart-items/cart-items.css";
import { CustomImage } from "checkout-experience/components/common/custom-image";
import { displayMonetaryValue } from "checkout-experience/components/common/monetary-util";
import { FormTitle } from "checkout-experience/components/forms/form-title";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";

import { RateMode } from "frontend-client";

interface CartItemsProps {
  hideImages?: boolean;
}

const CartItems = ({ hideImages = false }: CartItemsProps) => {
  const { t, i18n } = useTranslation(["combined"]);
  const { checkoutSession } = useChexContext();
  const { cartItems, orderSummary } = checkoutSession ?? {};

  const getDisplayedAmount = (amount: bigint): string =>
    displayMonetaryValue(
      {
        amount,
        currencyCode: orderSummary?.totalAmount?.currencyCode ?? "USD",
        decimalCount: 2,
      },
      i18n.language
    );

  const contentTableRows = cartItems?.map((item, i) => {
    const hasDiscount =
      item.discounts?.length > 0 &&
      item.discounts[0].transactionDiscountAmount !== BigInt(0);
    const discount = item.discounts?.[0];
    const itemPriceStr: JSX.Element | string =
      item.unitPrice === BigInt(0) ? (
        <Text styleVariant="heading4" tagElement="h3" colorVariant="positive">
          {t("cartItems.free")}
        </Text>
      ) : (
        <div className={cartItemPriceStringWrapper}>
          {hasDiscount && (
            <Text
              styleVariant="heading4"
              tagElement="h3"
              colorVariant="primaryText"
            >
              {getDisplayedAmount(item.totalFeeAmount)}
            </Text>
          )}
          <Text
            styleVariant="heading4"
            tagElement="h3"
            colorVariant={hasDiscount ? "secondaryText" : "primaryText"}
            style={hasDiscount ? { textDecoration: "line-through" } : undefined}
          >
            {getDisplayedAmount(item.unitPrice * item.quantity)}
          </Text>
          {hasDiscount && (
            <Text
              styleVariant={"heading4"}
              tagElement="h3"
              colorVariant={"positive"}
            >
              {discount?.discountMode === RateMode.FLAT
                ? `(${getDisplayedAmount(discount?.transactionDiscountAmount)} ${t("off")})`
                : `(${discount.purchaseTransactionDiscountPercent}% ${t("off")})`}
            </Text>
          )}
        </div>
      );

    return (
      <div
        key={i}
        className={`${cartItemsRow} ${i > 0 ? cartItemsNonFirstRow : ""}`}
      >
        {!hideImages && (
          <div className={cartItemImageWrapper}>
            <CustomImage
              src={item.imageUrl}
              height={80}
              width={80}
              alt={item.name}
              className={cartItemImageOverride}
            />
          </div>
        )}
        <div className={cartItemInfo}>
          <Text
            styleVariant="heading4"
            tagElement="h3"
            colorVariant="primaryText"
          >
            {item.name}
          </Text>
          <Text styleVariant="caption" colorVariant="primaryText">
            {item?.description}
          </Text>
          {Number(item.quantity) > 0 && (
            <div className={cartItemPriceWrapper}>
              <Text styleVariant="caption" colorVariant="secondaryText">
                <>
                  {t("cartItems.quantity")}: {Number(item.quantity)}
                </>
              </Text>
              {itemPriceStr}
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <>
      <span id="cartItemsSectionHeaderId" hidden>
        {t("cartItems.title")}
      </span>
      <FormTitle title={t("cartItems.title")} />
      <div className={cartItemsTable}>{contentTableRows}</div>
    </>
  );
};

export default CartItems;
